import {
  currentFidelityTier,
  resolveFidelityTierColor,
} from './fidelity-tiers';
import type {
  FidelityActionType,
  FidelityCard,
  FidelityConf,
  FidelityOperation,
  FidelityThreshold,
} from '../types/fidelity.types';

export function getFidelityRowKey(row: FidelityConf): string {
  return String(row.fidelityId || row.fidelityCode);
}

export function normalizeTagNames(raw: unknown): string[] {
  if (!Array.isArray(raw)) {
    return [];
  }

  const names: string[] = [];

  for (const item of raw) {
    if (typeof item === 'string') {
      const name = item.trim();
      if (name) {
        names.push(name);
      }
      continue;
    }

    if (!item || typeof item !== 'object') {
      continue;
    }

    const record = item as Record<string, unknown>;
    const candidate =
      (typeof record.tagName === 'string' && record.tagName) ||
      (typeof record.name === 'string' && record.name) ||
      (typeof record.label === 'string' && record.label) ||
      '';
    const name = candidate.trim();
    if (name) {
      names.push(name);
    }
  }

  return names;
}

export function getActiveFidelityCount(rows: FidelityConf[]): number {
  return rows.filter((row) => row.active).length;
}

export function hasActiveFidelityConflict(
  rows: FidelityConf[],
  excludeFidelityId?: number,
): boolean {
  return rows.some(
    (row) =>
      row.active &&
      (excludeFidelityId == null || row.fidelityId !== excludeFidelityId),
  );
}

export function resolveFidelityActiveForSave(
  requestedActive: boolean,
  existingPrograms: FidelityConf[],
  excludeFidelityId?: number,
): boolean {
  if (!requestedActive) {
    return false;
  }

  return !hasActiveFidelityConflict(existingPrograms, excludeFidelityId);
}

export function getFidelityProgramColor(
  row: Pick<FidelityConf, 'historicalTiers'>,
): string {
  const tier = currentFidelityTier(row.historicalTiers ?? [], 0);
  return tier ? resolveFidelityTierColor(tier.color).accent : '#64748b';
}

export function formatFidelityCodeLine(
  row: FidelityConf,
  labels: {
    range: (from: string, to: string) => string;
    noThresholds: string;
  },
): string {
  if (row.thresholds.length === 0) {
    return labels.noThresholds;
  }

  const lowerBounds = row.thresholds.map(
    (threshold) => threshold.lowerBound ?? 0,
  );
  const min = Math.min(...lowerBounds);
  const openEnded = row.thresholds.some(
    (threshold) => threshold.upperBound == null,
  );
  const max = Math.max(
    ...row.thresholds
      .map((threshold) => threshold.upperBound)
      .filter((upper): upper is number => upper !== undefined),
  );

  return labels.range(String(min), openEnded ? '∞' : String(max));
}

export function getFidelityThresholdCount(row: FidelityConf): number {
  return row.thresholds.length;
}

export function getDefaultThresholds(hexColor: string): Array<{
  lowerBound: number;
  upperBound?: number;
  points: number;
  hexColor: string;
}> {
  return [
    {
      lowerBound: 0,
      upperBound: 49,
      points: 5,
      hexColor,
    },
    {
      lowerBound: 50,
      upperBound: undefined,
      points: 10,
      hexColor,
    },
  ];
}

export function formatThresholdRange(lower: number, upper?: number): string {
  return upper == null
    ? `Da ${String(lower)} euro in su`
    : `${String(lower)} - ${String(upper)} euro`;
}

export function getNextThresholdDraft(
  thresholds: FidelityThreshold[],
  hexColor: string,
): FidelityThreshold {
  const lastThreshold = [...thresholds]
    .sort((left, right) => (left.lowerBound ?? 0) - (right.lowerBound ?? 0))
    .at(-1);
  const nextLowerBound = lastThreshold
    ? (lastThreshold.lowerBound ?? 0) + 50
    : 0;

  return {
    lowerBound: nextLowerBound,
    upperBound: undefined,
    points: lastThreshold?.points ?? 0,
    hexColor,
  };
}

export function formatFidelityCustomerName(card: FidelityCard): string {
  const firstName = card.customerDTO?.firstName?.trim() ?? '';
  const lastName = card.customerDTO?.lastName?.trim() ?? '';
  const fullName = `${firstName} ${lastName}`.trim();

  return (
    fullName || card.customerDTO?.customerCode || `#${String(card.customerId)}`
  );
}

export function formatFidelityCustomerContacts(card: FidelityCard): string {
  const email = card.customerDTO?.email?.trim();
  if (email) {
    return email;
  }

  const phone = card.customerDTO?.phone?.trim();
  if (phone) {
    const prefix = card.customerDTO?.prefix?.trim();
    return prefix ? `+${prefix} ${phone}` : phone;
  }

  return '—';
}

export function formatFidelityCardBalance(
  card: FidelityCard,
  labels: { points: string; amount: string },
): string {
  if (card.points > 0) {
    return `${String(card.points)} ${labels.points}`;
  }

  if (card.prepaidAmount > 0) {
    return `${String(card.prepaidAmount)} ${labels.amount}`;
  }

  return `0 ${labels.points}`;
}

export function getFidelityCardStateVariant(
  used: boolean,
): 'success' | 'warning' {
  return used ? 'warning' : 'success';
}

export function getFidelityCardStateLabel(
  used: boolean,
  labels: { used: string; available: string },
): string {
  return used ? labels.used : labels.available;
}

export function getFidelityOperationValue(
  operation: FidelityOperation,
): number {
  return operation.pointsAdded ?? operation.amountAdded ?? 0;
}

export function getFidelityOperationLabel(
  actionType: FidelityActionType | undefined,
  labels: Record<FidelityActionType, string>,
): string {
  if (!actionType) {
    return '—';
  }

  return labels[actionType];
}
